// @givmo/design-system — Checkbox (ports Flutter `CCheckBox`, ccheckbox.dart).
//
// Built on Base UI's Checkbox (@base-ui/react/checkbox). Ported visuals:
//   - 28×28 box (Flutter default size), radius all6 (the inline `BorderRadius.circular(6)`,
//     promoted to the `all6` token).
//   - checked fill = blue (#3C6DE5); unchecked = white box with a neutral-500 border (EP-WEB45: the
//     border is an unchecked box's only boundary, and Flutter's gray60 missed SC 1.4.11's 3:1).
//   - hover/active overlay = blue@10% (Flutter `blue.withOpacity(0.1)` overlayColor) — rendered
//     as the recommended `/10` Tailwind opacity utility per recon §5 alpha conventions.
//   - white check glyph (Lucide `Check`) shown via Checkbox.Indicator.
//   - Disabled = 50% opacity + the not-allowed cursor, keyed on Base UI's `data-disabled`. The root is
//     a `<span role="checkbox">`, which the `:disabled` pseudo-class (the `disabled:` variant) can
//     never match — a `disabled:` recipe painted every disabled checkbox exactly like an enabled one.
//
// NO INLINE `style` — the box is sized by CLASSES. The donate, pay and consent apps serve a
// nonce-only `style-src` ('self' + a per-request nonce, never 'unsafe-inline'), which refuses
// every server-rendered `style` attribute, and React never re-applies an unchanged style prop on
// hydrate — so a `style={{ width, height }}` root collapsed to a 2×2 px dot on every deployed
// host. `size` is therefore a CLOSED set mapped through {@link CHECKBOX_SIZE_CLASS} (Tailwind
// cannot emit an arbitrary value from a runtime number); an unsupported number is a type error,
// never a silent fallback. Base UI's visually-hidden native <input> (its own inline `style`) is
// hidden by the stylesheet rule in `src/styles/base-ui.css` instead. README: "No `style` prop".
//
// A11Y GOTCHA — do NOT pass `aria-label` when the checkbox sits inside a `<label>` that carries
// visible text. Base UI points `aria-labelledby` at that wrapping label, whose accessible text
// already includes this control's own `aria-label`, so the two STACK and a screen reader announces
// the label twice ("I accept … I accept …"). Wrapped-in-a-label ⇒ the visible text is the name;
// `aria-label` belongs only on a bare checkbox with no visible label (e.g. a select-all cell).
import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';
import { Check } from 'lucide-react';
import { cn } from '../lib/utils';
import { FOCUS_RING } from '../lib/focus';
import { testIdProp, type TestIdProps } from '../lib/testid';

/**
 * The box sizes in use across the estate, in px: 18 (the SaaS default), 24 (the donate and pay
 * consent rows), 28 (the Flutter default), 40 (the Storybook `Large` story). Extend the table to
 * add one — never pass a free number, Tailwind emits no class for it.
 */
export type CheckboxSize = 18 | 24 | 28 | 40;

/**
 * `size` → the class that sizes the root. The `size-[Npx]` arbitrary-value form is the house
 * pattern (`button.tsx`, `calendar.tsx`); every entry is a literal so the apps' Tailwind scan of
 * this package's source emits it.
 */
export const CHECKBOX_SIZE_CLASS: Record<CheckboxSize, string> = {
  18: 'size-[18px]',
  24: 'size-[24px]',
  28: 'size-[28px]',
  40: 'size-[40px]',
};

const LEGACY_DEFAULT_SIZE: CheckboxSize = 28;
const SAAS_DEFAULT_SIZE: CheckboxSize = 18;

export interface CheckboxProps extends TestIdProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  name?: string;
  required?: boolean;
  /**
   * Square size in px — one of the sizes in use ({@link CheckboxSize}). Defaults to 28 (Flutter
   * CCheckBox default), or 18 for the SaaS appearance.
   */
  size?: CheckboxSize;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /**
   * Visual appearance (added 2026-06-26, ADDITIVE — mirrors Button/Select/Tabs/Switch):
   *   `legacy` (default) → the Flutter-ported box (28px, blue fill) — unchanged.
   *   `saas`             → the fintech checkbox (18px, brand-600 fill, rounded, focus ring).
   * Same Base UI `role="checkbox"` + a11y in both.
   */
  appearance?: 'legacy' | 'saas';
}

export function Checkbox({
  checked,
  defaultChecked,
  onCheckedChange,
  disabled,
  name,
  required,
  size,
  className,
  testId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  appearance = 'legacy',
}: CheckboxProps) {
  const saas = appearance === 'saas';
  const boxSize: CheckboxSize = size ?? (saas ? SAAS_DEFAULT_SIZE : LEGACY_DEFAULT_SIZE);
  const sizeClass = CHECKBOX_SIZE_CLASS[boxSize];
  return (
    <BaseCheckbox.Root
      checked={checked}
      defaultChecked={defaultChecked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      name={name}
      required={required}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={
        saas
          ? cn(
              'inline-flex shrink-0 items-center justify-center rounded-sm border transition-colors',
              sizeClass,
              // Unchecked: surface box, neutral-500 border. An unchecked box has no fill to
              // identify it, so the border IS the control boundary and needs ≥3:1 (WCAG 1.4.11):
              // neutral-500 = 4.68:1 on white / 4.47:1 on surface-sunken. Checked: brand-600.
              'border-neutral-500 bg-surface',
              'data-[checked]:border-brand-600 data-[checked]:bg-brand-600',
              // Hover darkens rather than lightens — scoped to the unchecked state so it cannot
              // override the checked box's brand border (neutral-600 = 7.54:1 on white).
              'data-[unchecked]:hover:border-neutral-600 data-[checked]:hover:bg-brand-700',
              'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
              // The house focus pairing, published once as {@link FOCUS_RING} — the tinted ring
              // composites to 1.23:1 and cannot carry the indicator alone. `outline-none` stays
              // here (it drops the UA ring), and the constant re-arms `outline-style` after it.
              'outline-none',
              FOCUS_RING,
              className,
            )
          : cn(
              // `shrink-0`: the box is a flex item beside a paragraph on every consent row, and a
              // flex item shrinks below its own width by default — the 24px box measured ~14px
              // wide on the gift-card terms row before this.
              'inline-flex shrink-0 items-center justify-center rounded-all6 border transition-colors',
              sizeClass,
              // Unchecked: white box, neutral-500 border (the SaaS box's boundary above, 4.68:1;
              // gray60 was 2.65:1). Checked: blue fill + blue border.
              'border-neutral-500 bg-white',
              'data-[checked]:border-blue data-[checked]:bg-blue',
              // blue@10% overlay on hover (recon §5 alpha convention → /10 opacity utility).
              'hover:bg-blue/10 data-[checked]:hover:bg-blue',
              'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
              'outline-none focus-visible:shadow-focus-glow',
              className,
            )
      }
      {...testIdProp(testId)}
    >
      <BaseCheckbox.Indicator className="flex text-on-brand data-[unchecked]:hidden">
        <Check size={Math.round(boxSize * 0.7)} strokeWidth={3} aria-hidden />
      </BaseCheckbox.Indicator>
    </BaseCheckbox.Root>
  );
}
